import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import PlayCircleIcon from '@guildeducationinc/recess/icons/PlayCircleIcon';
import { RESUME_IMPORT } from '@src/graphql/queries/import';
import { useImports } from '@src/hooks/useImports';
import { ImportStatus } from '@src/types';
import { DataTable, MessageType, Messages, TrackableButton, useLocale, } from 'pe-core-frontend';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// An import that ended in error or quarantine is stopped on purpose: snapshot imports build
// on each other, and a routed import must not fan out again while a child run is unresolved.
// Restarting it is a deliberate step, so these are the only statuses that offer Resume.
const STOPPED_STATUSES = [ImportStatus.ERROR, ImportStatus.QUARANTINE];
export const Imports = ({ clientId }) => {
    const navigate = useNavigate();
    const { t, formatToLocalDatetime } = useLocale();
    const [messages, setMessages] = useState([]);
    const { items, loading, error, refetch } = useImports({
        clientId,
    });
    const [resumeImport] = useMutation(RESUME_IMPORT);
    const handleEdit = (row) => {
        navigate(`/admin/clients/${clientId}/integrations/imports/${row.id}`);
    };
    const handleResume = async (row) => {
        try {
            const result = await resumeImport({ variables: { id: row.id } });
            if (result.data?.resumeImport?.success) {
                await refetch();
            }
            setMessages(result.data.resumeImport.messages);
        }
        catch (ex) {
            setMessages([{ type: MessageType.ERROR, value: String(ex?.message ?? '') }]);
        }
    };
    return (_jsxs("div", { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(Messages, { messages: messages }), _jsx("div", { className: 'mb-4 flex flexJustify--end', children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Import', tag: 'imports_list', variant: 'contained', className: 'button button--primary margin--bottom_16', onClick: () => navigate(`/admin/clients/${clientId}/integrations/imports/new`), children: t('Add Import') }) }), _jsx(DataTable, { loading: loading, disableColumnMenu: true, disableRowSelectionOnClick: true, rows: items || [], hideFooter: true, columns: [
                    {
                        field: 'name',
                        headerName: t('Name'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'isActive',
                        headerName: t('Is Active?'),
                        flex: 0.2,
                        sortable: false,
                        renderCell: ({ row }) => (row.isActive ? 'Yes' : 'No'),
                    },
                    {
                        field: 'lastRunStatus',
                        headerName: t('Last Run Status'),
                        flex: 0.2,
                        sortable: false,
                        renderCell: ({ value }) => (value ? t(`ImportStatus.${value}`) : ''),
                    },
                    {
                        field: 'lastRunAt',
                        headerName: t('Last Run At'),
                        flex: 0.2,
                        sortable: false,
                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                    },
                    {
                        field: 'cronSchedules',
                        headerName: t('Schedules'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'action',
                        headerName: t('Action'),
                        renderCell: ({ row }) => (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `import_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => handleEdit(row), className: 'margin--right_8' }), row.lastRunStatus && STOPPED_STATUSES.includes(row.lastRunStatus) && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Resume', tag: `import_${row.id}`, label: t('Resume: clear the stopped status so the next scheduled run starts'), appearance: 'minimal', icon: _jsx(PlayCircleIcon, { size: 20 }), onClick: () => handleResume(row), className: 'margin--right_8' }))] })),
                        width: 100,
                        sortable: false,
                    },
                ] })] }));
};
